import * as React from 'react';
import { gql, useQuery } from '@apollo/client';
import classNames from 'classnames';
import get from 'lodash/get';
import { PillsFilter } from '@quality24/design-system';

import {
  PillsFilterComponentData,
  FilterUsageOption,
  GraphQLFilter,
} from '../../../../@types';
import { useQueryFilter } from '../../../../hooks/filterHooks';
import { parseDataValue } from '../../../../utils/dataUtils';
import {
  parseFilterData,
  formatFilterData,
  mergePermissionFilter,
} from '../../../../utils/filterUtils';

const MockQuery = gql`
  query MockQueryNotToBePerformed {
    apiVersion {
      versionName
    }
  }
`;

export interface Props {
  className?: string;
  partitionId?: 'default' | string;
  config: PillsFilterComponentData;
  baseFilter?: GraphQLFilter;
  permissionFilter?: GraphQLFilter;
}

/**
 * <PillsFilterContainer> page
 */
export const PillsFilterContainer: React.FunctionComponent<Props> = ({
  className,
  partitionId = 'default',
  baseFilter = {},
  permissionFilter: userPermissionFilter = {},
  config: {
    id,
    className: configClassName,
    variant,
    align = 'left',
    innerFilters,
    customFilter,
    permissionFilters,
    query,
    parser,
    parserOptions,
    options,
    defaultValue,
  },
}) => {
  const { filters, upsertFilter, removeFilter } = useQueryFilter(
    [id],
    partitionId,
  );

  // Handle inner filters
  const { getGraphQLFilter, getPermissionFilter } = useQueryFilter(
    innerFilters?.map((f) => (typeof f === 'string' ? f : f.id)) || [],
    partitionId,
  );

  // Retrieve and memoize the graphQLFilters
  // This getter applies any usage modifications
  const graphQLFilter = React.useMemo(
    () =>
      getGraphQLFilter(
        innerFilters?.filter(
          (f) => typeof f !== 'string',
        ) as FilterUsageOption[],
      ),
    [getGraphQLFilter, innerFilters],
  );

  // Merge all filters for the query
  const queryFilter = React.useMemo(() => {
    if (
      (baseFilter && Object.keys(baseFilter).length > 0) ||
      (graphQLFilter && Object.keys(graphQLFilter).length > 0) ||
      (permissionFilters && permissionFilters.length > 0) ||
      (customFilter && Object.keys(customFilter).length > 0)
    ) {
      const permissionFilter = getPermissionFilter(
        userPermissionFilter,
        permissionFilters,
      );

      const parsedFilters = mergePermissionFilter(
        {
          ...baseFilter,
          ...graphQLFilter,
          ...customFilter,
        },
        permissionFilter,
      );

      return Object.keys(parsedFilters).length ? parsedFilters : undefined;
    }
    return undefined;
  }, [
    baseFilter,
    customFilter,
    getPermissionFilter,
    graphQLFilter,
    permissionFilters,
    userPermissionFilter,
  ]);

  // Prepare the query to eventually fetch filter
  const { data, loading } = useQuery(query ? gql([query]) : MockQuery, {
    skip: !query,
    variables: { filter: queryFilter },
  });

  // Build the filter options
  const filterOptions = React.useMemo(
    () =>
      options
        .flatMap((opt) => {
          if (!opt.rootKey) return opt;
          if (!data) return null;

          return get(data, opt.rootKey, []).map(
            (n: Record<string, unknown>) => ({
              key: opt.key
                ? parseDataValue(n, opt.key, opt.dataType || 'string')
                : undefined,
              id: parseDataValue(n, opt.id, opt.dataType || 'string'),
              label: parseDataValue(n, opt.label, opt.dataType || 'string'),
            }),
          );
        })
        .filter((opt) => opt),
    [options, data],
  );

  // Parse query filter format. Pills are single-select, so collapse an
  // array-valued parser (e.g. in-months / in-array) to its first entry — the
  // stored value stays an array, but the widget needs a scalar to highlight.
  const value = React.useMemo(() => {
    const filterValue = filters[id];
    if (filterValue) {
      const parsed = parseFilterData(parser, filterValue, { parserOptions });
      if (parsed) return Array.isArray(parsed) ? parsed[0] ?? '' : parsed;
    }
    return '';
  }, [filters, parser, parserOptions, id]);

  // Set the initial value of the filter — the configured `defaultValue` when
  // provided, otherwise the first option (pills always keep one selected).
  React.useEffect(() => {
    if (query && loading) return;
    const formatted = formatFilterData(
      parser,
      defaultValue ?? filterOptions[0]?.id,
      { parserOptions },
    );
    if (formatted) {
      upsertFilter(id, formatted);
    }
    // @note: we want this hook to run only once after the query, that is why
    //        we disabled the lint rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, loading, filterOptions]);

  /**
   * Handles the filter change, saving it the the context
   */
  const handleFilterChange = React.useCallback(
    (name: string, newValue: string) => {
      if (newValue === '') {
        removeFilter(name);
        return;
      }

      const formatted = formatFilterData(parser, newValue, {
        parserOptions,
      });
      if (formatted) {
        upsertFilter(name, formatted);
      }
    },
    [upsertFilter, removeFilter, parser, parserOptions],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('PillsFilterContainer', {
      value,
      filterOptions,
      filterContext: filters,
    });
  }

  return (
    <PillsFilter
      className={classNames(className, configClassName)}
      name={id}
      value={value}
      options={filterOptions}
      variant={variant}
      align={align}
      onChange={handleFilterChange}
    />
  );
};

export default PillsFilterContainer;
